import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mail, ArrowRight, BookOpen, FileText, CheckCircle2 } from "lucide-react";

// ============================================================================
//  /start-success — Confirmation page after the calendar opt-in.
//  MailerLite handles the actual calendar delivery via automation.
//  This page just confirms + warms the lead with next-step CTAs.
// ============================================================================

const OCEAN = {
  deep: "#1B6B6E",
  mid: "#3D9096",
  light: "#7BC8CB",
  cream: "#F4F1EC",
  mint: "#EBF6F6",
  dark: "#1C2B2B",
};

export default function StartSuccess() {
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "start_success_view");
    }
  }, []);

  return (
    <div className="min-h-screen" style={{ background: OCEAN.cream }} data-testid="start-success-page">
      <Helmet>
        <title>Check Your Inbox — Your 12-Month I GET TO Calendar Is on Its Way</title>
        <meta name="description" content="Your free calendar is in your inbox. While you wait — meet Temple, read the blog, or grab the book." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <section
        className="pt-20 md:pt-24 pb-12 md:pb-16 px-5 md:px-8 text-center"
        style={{ background: `linear-gradient(165deg, ${OCEAN.deep} 0%, ${OCEAN.mid} 100%)` }}
        data-testid="start-success-hero"
      >
        <div className="max-w-2xl mx-auto" style={{ color: OCEAN.cream }}>
          <CheckCircle2
            className="w-16 h-16 mx-auto mb-5"
            style={{ color: OCEAN.light }}
            data-testid="start-success-icon"
          />
          <p
            className="font-sans text-[10px] md:text-[11px] tracking-[0.35em] uppercase font-semibold mb-3"
            style={{ color: OCEAN.light }}
          >
            ✦ You're in, Lovely
          </p>
          <h1
            className="font-serif tracking-tighter leading-[1.05] mb-5"
            style={{ fontSize: "clamp(1.85rem, 5vw, 3rem)" }}
            data-testid="start-success-headline"
          >
            Your calendar is on its way.
          </h1>
          <p
            className="font-serif italic text-base md:text-lg mb-7 max-w-xl mx-auto"
            style={{ color: "rgba(244,241,236,0.88)" }}
            data-testid="start-success-subhead"
          >
            Check your inbox in the next few minutes. Your free 12-Month I GET TO Calendar will land there shortly — and we'll keep in touch with real tools, gentle nudges, and the occasional note from the messy middle.
          </p>
          <p
            className="font-sans text-xs md:text-sm"
            style={{ color: "rgba(244,241,236,0.72)" }}
            data-testid="start-success-spam-note"
          >
            <Mail className="inline w-4 h-4 mr-1 -mt-0.5" />
            Don't see it? Peek in your <strong>Promotions</strong> tab or spam folder, and add <em>mindbodyspirithygiene@gmail.com</em> to your contacts.
          </p>
        </div>
      </section>

      {/* ─── While you wait... ─── */}
      <section className="px-5 md:px-8 py-12 md:py-16" data-testid="start-success-next">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-[10px] tracking-[0.35em] uppercase font-semibold text-center mb-3"
            style={{ color: OCEAN.light }}
          >
            While you wait
          </p>
          <h2
            className="font-serif text-2xl md:text-4xl text-center mb-10 tracking-tighter"
            style={{ color: OCEAN.deep }}
          >
            Three places worth wandering.
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            {/* The Book */}
            <Link
              to="/book"
              data-testid="start-success-link-book"
              className="block p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all hover:-translate-y-1"
              style={{ background: "white" }}
            >
              <BookOpen className="w-8 h-8 mb-4" style={{ color: OCEAN.deep }} />
              <h3 className="font-serif text-xl md:text-2xl mb-2 leading-tight" style={{ color: OCEAN.deep }}>
                The Book
              </h3>
              <p className="font-serif italic text-sm mb-4" style={{ color: OCEAN.mid }}>
                Stop saying "I have to." Start living "I GET TO."
              </p>
              <span className="font-sans text-xs tracking-[0.28em] uppercase font-bold" style={{ color: OCEAN.deep }}>
                See the book →
              </span>
            </Link>

            {/* The Blog */}
            <Link
              to="/blog"
              data-testid="start-success-link-blog"
              className="block p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all hover:-translate-y-1"
              style={{ background: "white" }}
            >
              <FileText className="w-8 h-8 mb-4" style={{ color: OCEAN.mid }} />
              <h3 className="font-serif text-xl md:text-2xl mb-2 leading-tight" style={{ color: OCEAN.deep }}>
                The Blog
              </h3>
              <p className="font-serif italic text-sm mb-4" style={{ color: OCEAN.mid }}>
                Essays from the messy middle.
              </p>
              <span className="font-sans text-xs tracking-[0.28em] uppercase font-bold" style={{ color: OCEAN.deep }}>
                Read essays →
              </span>
            </Link>

            {/* Meet Temple */}
            <Link
              to="/about"
              data-testid="start-success-link-about"
              className="block p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all hover:-translate-y-1"
              style={{ background: "white" }}
            >
              <ArrowRight className="w-8 h-8 mb-4" style={{ color: OCEAN.deep }} />
              <h3 className="font-serif text-xl md:text-2xl mb-2 leading-tight" style={{ color: OCEAN.deep }}>
                Meet Temple
              </h3>
              <p className="font-serif italic text-sm mb-4" style={{ color: OCEAN.mid }}>
                The story behind the method.
              </p>
              <span className="font-sans text-xs tracking-[0.28em] uppercase font-bold" style={{ color: OCEAN.deep }}>
                Read her story →
              </span>
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-5 py-8 border-t" style={{ borderColor: "rgba(27,107,110,0.12)" }}>
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold"
            style={{ color: OCEAN.deep, opacity: 0.7 }}
          >
            © Mind Body Spirit HYGIENE™ · Temple Franklin
          </p>
        </div>
      </footer>
    </div>
  );
}
